import { midiHz, type NoteEvent } from "./music-score.ts";
import { VIOLA_HZ } from "./recipes.ts";

/**
 * Instrumentos da trilha, tocados ao vivo pelo sequenciador (doc 05): sanfona (pares de serra
 * desafinados com vibrato), pífano (seno + sopro), drone e baixo saturado; zabumba, triângulo,
 * bumbo, caixa e viola (Karplus-Strong) vêm das amostras pré-renderizadas.
 */
export interface Samples {
  get(id: "zabumba" | "triangulo" | "bumbo" | "caixa" | "viola"): AudioBuffer | undefined;
  noise: AudioBuffer | undefined;
}

function envelope(ctx: BaseAudioContext, t: number, peak: number, a: number, len: number, rel: number): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(peak, t + a);
  g.gain.setValueAtTime(peak, t + Math.max(a, len));
  g.gain.linearRampToValueAtTime(0.0001, t + Math.max(a, len) + rel);
  return g;
}

let shaper: Float32Array<ArrayBuffer> | undefined;
function driveCurve(): Float32Array<ArrayBuffer> {
  if (!shaper) {
    shaper = new Float32Array(512);
    for (let i = 0; i < shaper.length; i++) shaper[i] = Math.tanh(((i / 511) * 2 - 1) * 4);
  }
  return shaper;
}

/** Agenda uma nota em `when` (s do contexto); `beat` é a duração de um tempo em s. */
export function playNote(ctx: BaseAudioContext, out: AudioNode, e: NoteEvent, when: number, beat: number, samples: Samples): void {
  const len = e.dur * beat;
  const f = midiHz(e.midi);
  switch (e.inst) {
    case "zabumba":
    case "triangulo":
    case "bumbo":
    case "caixa":
    case "viola": {
      const b = samples.get(e.inst);
      if (!b) return;
      const src = ctx.createBufferSource();
      src.buffer = b;
      if (e.inst === "viola") src.playbackRate.value = f / VIOLA_HZ;
      const g = ctx.createGain();
      g.gain.value = e.vel;
      src.connect(g).connect(out);
      src.start(when);
      return;
    }
    case "sanfona": {
      const g = envelope(ctx, when, e.vel * 0.18, 0.04, len, 0.12);
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 2200;
      const vib = ctx.createOscillator();
      vib.frequency.value = 5.5;
      const depth = ctx.createGain();
      depth.gain.value = 4;
      vib.connect(depth);
      for (const cents of [-7, 7]) {
        const o = ctx.createOscillator();
        o.type = "sawtooth";
        o.frequency.value = f;
        o.detune.value = cents;
        depth.connect(o.detune);
        o.connect(lp);
        o.start(when);
        o.stop(when + len + 0.15);
      }
      vib.start(when);
      vib.stop(when + len + 0.15);
      lp.connect(g).connect(out);
      return;
    }
    case "pifano": {
      const g = envelope(ctx, when, e.vel * 0.22, 0.06, len, 0.15);
      const o = ctx.createOscillator();
      o.frequency.value = f;
      o.connect(g);
      o.start(when);
      o.stop(when + len + 0.2);
      if (samples.noise) {
        const n = ctx.createBufferSource();
        n.buffer = samples.noise;
        n.loop = true;
        const bp = ctx.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = f * 2;
        bp.Q.value = 2;
        const ng = ctx.createGain();
        ng.gain.value = 0.15;
        n.connect(bp).connect(ng).connect(g);
        n.start(when, Math.random());
        n.stop(when + len + 0.2);
      }
      g.connect(out);
      return;
    }
    case "drone": {
      const g = envelope(ctx, when, e.vel * 0.2, Math.min(2, len / 3), len, 1.5);
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 400;
      for (const m of [0, 7]) {
        const o = ctx.createOscillator();
        o.type = "sawtooth";
        o.frequency.value = midiHz(e.midi + m);
        o.connect(lp);
        o.start(when);
        o.stop(when + len + 1.6);
      }
      lp.connect(g).connect(out);
      return;
    }
    case "baixo": {
      const g = envelope(ctx, when, e.vel * 0.3, 0.01, len * 0.8, 0.08);
      const o = ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.value = f;
      const ws = ctx.createWaveShaper();
      ws.curve = driveCurve();
      const lp = ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 600;
      o.connect(ws).connect(lp).connect(g).connect(out);
      o.start(when);
      o.stop(when + len + 0.1);
      return;
    }
  }
}
